import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from '../../config/data-services/config.abstract.data-service';
import { ImageFitMode } from '../../config/entities/picture-fill-mode.entity';
import { AdminGuard } from '../../core/users/guards/admin-guard';
import { ActionMode } from '../generic-overview/components/context-action-bar/entities/select-mode.entity';
import { DisplayMode } from '../generic-overview/components/display-settings-bar/entities/display-mode.entity';
import { SortInfo } from '../generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginationInfo } from '../generic-overview/components/pagination/entities/pagination-info.entity';
import { Panel, PartialSearchState } from './entities/search-page-state.entity';
import { SearchPageStateService } from './state-machines/search-page-state.service';

@Injectable({ providedIn: 'root' })
export class SearchPageSandbox {
    private readonly searchPageStateService: SearchPageStateService = inject(SearchPageStateService);
    private readonly configService: ConfigService<any> = inject(ConfigService);

    protected readonly adminGuard: AdminGuard = inject(AdminGuard);

    public get $imageFitSetting(): Signal<ImageFitMode> {
        return toSignal(this.configService.get('imageFitMode'));
    }

    public batchSelectAllowed(batchSelectEnabled: boolean): Observable<boolean> {
        return this.adminGuard.hasAccess().pipe(map(isAdmin => batchSelectEnabled && isAdmin));
    }

    // #region State
    public restoreStoredUrlState(searchPageId: string): void {
        this.searchPageStateService.restoreStoredUrlState(searchPageId);
    }

    public getSearchState(): Signal<PartialSearchState> {
        return this.searchPageStateService.getSearchState();
    }

    public getStoredSelected(searchPageId: string): string[] {
        return this.searchPageStateService.getStoredSelected(searchPageId);
    }

    public getStoredActionMode(searchPageId: string): ActionMode {
        return this.searchPageStateService.getStoredActionMode(searchPageId);
    }

    public getPanelsState(searchPageId: string): Signal<Record<Panel, boolean>> {
        return this.searchPageStateService.getPanelsState(searchPageId);
    }

    public getPanelsStateStream(searchPageId: string): Observable<Record<Panel, boolean>> {
        return this.searchPageStateService.getPanelStateStream(searchPageId);
    }

    public savePaginationState(paginationInfo: PaginationInfo): void {
        this.searchPageStateService.savePaginationState(paginationInfo);
    }

    public saveSortState(sort: SortInfo): void {
        this.searchPageStateService.saveSortState(sort);
    }

    public saveViewState(view: DisplayMode): void {
        this.searchPageStateService.saveViewState(view);
    }

    public saveUrlState(searchPageId: string) {
        this.searchPageStateService.saveUrlState(searchPageId);
    }

    public saveSelectedState(searchPageId: string, selectedItemIds: string[]): void {
        this.searchPageStateService.saveSelectedState(searchPageId, selectedItemIds);
    }

    public saveActionModeState(searchPageId: string, actionMode: ActionMode): void {
        this.searchPageStateService.saveActionModeState(searchPageId, actionMode);
    }

    public savePanelState(searchPageId: string, panel: Panel, open: boolean): void {
        this.searchPageStateService.savePanelState(searchPageId, panel, open);
    }
    // #endregion
}
